/**
 * MLB SPOILER TRACKER
 * Ronde 3: volledige thema's per team — kleuren, logo, CSS-variabelen.
 * Bij het wisselen van team worden CSS custom properties dynamisch
 * overschreven op document.documentElement zodat de hele app meekleurt.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc }                         from 'firebase/firestore';
import { auth, provider, db }                          from './firebase';
import { Analytics }                                   from '@vercel/analytics/react';
import './Twins-app.css';

// ─── SEIZOEN ──────────────────────────────────────────────────────────────────

const SEASON     = 2026;
const BASE_URL   = 'https://statsapi.mlb.com/api/v1';

// ─── SECURITY: URL VALIDATIE ──────────────────────────────────────────────────

// Alleen afbeeldingen van officiële MLB-domeinen worden getoond.
// Dit voorkomt tracking-pixels en kwaadaardige afbeeldingen via de RSS-feed.
const ALLOWED_IMG_DOMAINS = [
  'mlb.com', 'mlbstatic.com', 'img.mlbstatic.com',
  'content.mlb.com', 'securea.mlb.com', 'secureb.mlb.com',
];

function isSafeImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const { hostname, protocol } = new URL(url);
    if (!['https:', 'http:'].includes(protocol)) return false;
    return ALLOWED_IMG_DOMAINS.some(d => hostname === d || hostname.endsWith('.' + d));
  } catch { return false; }
}

// Alleen https:// en http:// links zijn toegestaan.
// Dit blokkeert javascript: URLs die XSS kunnen veroorzaken.
function isSafeUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const { protocol } = new URL(url);
    return ['https:', 'http:'].includes(protocol);
  } catch { return false; }
}

// rss2json werkt anders: geeft JSON terug, geen XML
// Gebruikt als aparte fallback naast de XML-proxy's
const RSS2JSON_URL = 'https://api.rss2json.com/v1/api.json?rss_url=';

// Haal de ruwe RSS-XML op via een reeks proxy's
async function fetchRssXml(feedUrl) {
  // Extraheer de slug uit de feedUrl
  const slug = feedUrl.match(/mlb\.com\/([^/]+)\//)?.[1] || 'twins';
  
  // Gebruik onze eigen Vercel API route
  try {
    const res = await fetch(`/api/news?slug=${slug}`, { 
      signal: AbortSignal.timeout(8000) 
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    if (text.includes('<rss') || text.includes('<feed')) return text;
    throw new Error('Geen geldige RSS XML');
  } catch (e) {
    throw new Error(`API route mislukt: ${e.message}`);
  }
}

// Parseer ruwe RSS-XML naar een array van nieuws-objecten
function parseRssXml(xml, team) {
  const parser  = new DOMParser();
  const doc     = parser.parseFromString(xml, 'text/xml');
  const items   = Array.from(doc.querySelectorAll('item'));

  return items.map(item => {
    const get  = tag => item.querySelector(tag)?.textContent?.trim() || '';
    const title = get('title');
    const link  = get('link') || item.querySelector('link')?.getAttribute('href') || '';
    const rawDate = get('pubDate') || get('published') || get('updated') || '';
    const pubDate = parseRssDate(rawDate);

    const rawDesc = get('description') || get('content') || get('summary') || '';
    const text = rawDesc
      .replace(/<\/p>/gi, '\n\n').replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'").replace(/\n{3,}/g, '\n\n').trim();

    // Afbeelding uit media:content, enclosure, of img-tag in beschrijving
    let image = item.querySelector('media\\:content, content')?.getAttribute('url')
      || item.querySelector('enclosure')?.getAttribute('url')
      || null;
    if (!image && rawDesc) {
      const m = rawDesc.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (m) image = m[1];
    }

    return { title, link, pubDate, category: `${team.abbr} NEWS`, description: text, image };
  }).filter(item => item.title); // verwijder lege items
}

// ─── TEAM THEMA'S ─────────────────────────────────────────────────────────────
// primary   = hoofdkleur (header border, knoppen, accenten)
// secondary = tweede kleur (highlights)
// bg        = donkere achtergrond (body)
// surface   = paneelachtergrond
// surface2  = paneel-headerachtergrond

const TEAM_THEMES = {
  BAL: { primary: '#DF4601', secondary: '#000000', bg: '#150800', surface: '#1f1000', surface2: '#2a1600' },
  BOS: { primary: '#BD3039', secondary: '#0D2B56', bg: '#0d0205', surface: '#130308', surface2: '#1a040b' },
  NYY: { primary: '#C4CED4', secondary: '#003087', bg: '#050607', surface: '#0a0c0e', surface2: '#101416' },
  TB:  { primary: '#8FBCE6', secondary: '#092C5C', bg: '#010810', surface: '#020c18', surface2: '#031120' },
  TOR: { primary: '#134A8E', secondary: '#E8291C', bg: '#010a18', surface: '#02102a', surface2: '#03163a' },
  CWS: { primary: '#C4CED4', secondary: '#27251F', bg: '#050505', surface: '#0d0d0d', surface2: '#151515' },
  CLE: { primary: '#E31937', secondary: '#00385D', bg: '#0d0004', surface: '#160008', surface2: '#1f000c' },
  DET: { primary: '#FA4616', secondary: '#0C2340', bg: '#0d0300', surface: '#1a0700', surface2: '#230a00' },
  KC:  { primary: '#BD9B60', secondary: '#004687', bg: '#0d0900', surface: '#1a1200', surface2: '#231900' },
  MIN: { primary: '#D31145', secondary: '#002B5C', bg: '#001a38', surface: '#0a1e3a', surface2: '#0f2547' },
  HOU: { primary: '#EB6E1F', secondary: '#002D62', bg: '#0d0500', surface: '#1a0b00', surface2: '#231100' },
  LAA: { primary: '#BA0021', secondary: '#003263', bg: '#0d0002', surface: '#150003', surface2: '#1d0005' },
  OAK: { primary: '#EFB21E', secondary: '#003831', bg: '#0d0900', surface: '#1a1200', surface2: '#231900' },
  SEA: { primary: '#005C5C', secondary: '#0C2C56', bg: '#000d0d', surface: '#001a1a', surface2: '#002424' },
  TEX: { primary: '#003278', secondary: '#C0111F', bg: '#00040f', surface: '#000920', surface2: '#000e2e' },
  ATL: { primary: '#CE1141', secondary: '#13274F', bg: '#0d0006', surface: '#16000b', surface2: '#1f0010' },
  MIA: { primary: '#00A3E0', secondary: '#EF3340', bg: '#00131a', surface: '#001f2a', surface2: '#002b3a' },
  NYM: { primary: '#FF5910', secondary: '#002D72', bg: '#0d0400', surface: '#1a0800', surface2: '#230b00' },
  PHI: { primary: '#E81828', secondary: '#002D72', bg: '#0d0002', surface: '#150003', surface2: '#1e0005' },
  WSH: { primary: '#AB0003', secondary: '#14225A', bg: '#0d0000', surface: '#150000', surface2: '#1e0001' },
  CHC: { primary: '#0E3386', secondary: '#CC3433', bg: '#020818', surface: '#030f28', surface2: '#041536' },
  CIN: { primary: '#C6011F', secondary: '#000000', bg: '#0d0002', surface: '#150003', surface2: '#1e0004' },
  MIL: { primary: '#B6922E', secondary: '#12284B', bg: '#0d0900', surface: '#1a1200', surface2: '#231900' },
  PIT: { primary: '#FDB827', secondary: '#27251F', bg: '#0d0a00', surface: '#1a1400', surface2: '#231c00' },
  STL: { primary: '#C41E3A', secondary: '#0C2340', bg: '#0d0205', surface: '#150308', surface2: '#1e040b' },
  ARI: { primary: '#A71930', secondary: '#E3D4AD', bg: '#0d0204', surface: '#150307', surface2: '#1e040a' },
  COL: { primary: '#33006F', secondary: '#C4CED4', bg: '#08000f', surface: '#100018', surface2: '#170021' },
  LAD: { primary: '#005A9C', secondary: '#EF3E42', bg: '#000d18', surface: '#001830', surface2: '#002040' },
  SD:  { primary: '#FFC425', secondary: '#2F241D', bg: '#0d0a00', surface: '#1a1400', surface2: '#231c00' },
  SF:  { primary: '#FD5A1E', secondary: '#27251F', bg: '#0d0500', surface: '#1a0b00', surface2: '#231100' },
};

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function applyTheme(abbr) {
  const theme = TEAM_THEMES[abbr] || TEAM_THEMES['MIN'];
  const root  = document.documentElement;
  root.style.setProperty('--team-primary',        theme.primary);
  root.style.setProperty('--team-secondary',      theme.secondary);
  root.style.setProperty('--team-bg',             theme.bg);
  root.style.setProperty('--team-surface',        theme.surface);
  root.style.setProperty('--team-surface2',       theme.surface2);
  root.style.setProperty('--team-primary-dim',    hexToRgba(theme.primary, 0.15));
  root.style.setProperty('--team-primary-glow',   hexToRgba(theme.primary, 0.35));
  root.style.setProperty('--team-primary-border', hexToRgba(theme.primary, 0.45));
}

// ─── DIVISIES & TEAMS ─────────────────────────────────────────────────────────

const DIVISIONS = {
  201: { name: 'AL East',    leagueId: 103 },
  202: { name: 'AL Central', leagueId: 103 },
  203: { name: 'AL West',    leagueId: 103 },
  204: { name: 'NL East',    leagueId: 104 },
  205: { name: 'NL Central', leagueId: 104 },
  206: { name: 'NL West',    leagueId: 104 },
};

const ALL_TEAMS = [
  { id: 110, abbr: 'BAL', name: 'Baltimore Orioles',    divisionId: 201, slug: 'orioles'   },
  { id: 111, abbr: 'BOS', name: 'Boston Red Sox',        divisionId: 201, slug: 'redsox'    },
  { id: 147, abbr: 'NYY', name: 'New York Yankees',      divisionId: 201, slug: 'yankees'   },
  { id: 139, abbr: 'TB',  name: 'Tampa Bay Rays',        divisionId: 201, slug: 'rays'      },
  { id: 141, abbr: 'TOR', name: 'Toronto Blue Jays',     divisionId: 201, slug: 'bluejays'  },
  { id: 145, abbr: 'CWS', name: 'Chicago White Sox',     divisionId: 202, slug: 'whitesox'  },
  { id: 114, abbr: 'CLE', name: 'Cleveland Guardians',   divisionId: 202, slug: 'guardians' },
  { id: 116, abbr: 'DET', name: 'Detroit Tigers',        divisionId: 202, slug: 'tigers'    },
  { id: 118, abbr: 'KC',  name: 'Kansas City Royals',    divisionId: 202, slug: 'royals'    },
  { id: 142, abbr: 'MIN', name: 'Minnesota Twins',       divisionId: 202, slug: 'twins'     },
  { id: 117, abbr: 'HOU', name: 'Houston Astros',        divisionId: 203, slug: 'astros'    },
  { id: 108, abbr: 'LAA', name: 'Los Angeles Angels',    divisionId: 203, slug: 'angels'    },
  { id: 133, abbr: 'OAK', name: 'Oakland Athletics',     divisionId: 203, slug: 'athletics' },
  { id: 136, abbr: 'SEA', name: 'Seattle Mariners',      divisionId: 203, slug: 'mariners'  },
  { id: 140, abbr: 'TEX', name: 'Texas Rangers',         divisionId: 203, slug: 'rangers'   },
  { id: 144, abbr: 'ATL', name: 'Atlanta Braves',        divisionId: 204, slug: 'braves'    },
  { id: 146, abbr: 'MIA', name: 'Miami Marlins',         divisionId: 204, slug: 'marlins'   },
  { id: 121, abbr: 'NYM', name: 'New York Mets',         divisionId: 204, slug: 'mets'      },
  { id: 143, abbr: 'PHI', name: 'Philadelphia Phillies', divisionId: 204, slug: 'phillies'  },
  { id: 120, abbr: 'WSH', name: 'Washington Nationals',  divisionId: 204, slug: 'nationals' },
  { id: 112, abbr: 'CHC', name: 'Chicago Cubs',          divisionId: 205, slug: 'cubs'      },
  { id: 113, abbr: 'CIN', name: 'Cincinnati Reds',       divisionId: 205, slug: 'reds'      },
  { id: 158, abbr: 'MIL', name: 'Milwaukee Brewers',     divisionId: 205, slug: 'brewers'   },
  { id: 134, abbr: 'PIT', name: 'Pittsburgh Pirates',    divisionId: 205, slug: 'pirates'   },
  { id: 138, abbr: 'STL', name: 'St. Louis Cardinals',   divisionId: 205, slug: 'cardinals' },
  { id: 109, abbr: 'ARI', name: 'Arizona Diamondbacks',  divisionId: 206, slug: 'dbacks'    },
  { id: 115, abbr: 'COL', name: 'Colorado Rockies',      divisionId: 206, slug: 'rockies'   },
  { id: 119, abbr: 'LAD', name: 'Los Angeles Dodgers',   divisionId: 206, slug: 'dodgers'   },
  { id: 135, abbr: 'SD',  name: 'San Diego Padres',      divisionId: 206, slug: 'padres'    },
  { id: 137, abbr: 'SF',  name: 'San Francisco Giants',  divisionId: 206, slug: 'giants'    },
];
const TRANSLATIONS = {
  en: {
    games: 'Games', standings: 'Standings', news: 'News', stats: 'Stats',
    home: 'HOME', away: 'AWAY', seen: 'Seen', unseen: 'Unseen', all: 'All',
    markAll: 'Mark all', reset: 'Reset', sure: 'Sure?',
    loading: 'Loading…', error: 'Data could not be loaded.',
    noGames: 'No games found.',
    lockStandings: 'Mark games as seen to unlock standings',
    lockStats: 'Mark games as seen to unlock player stats',
    lockNews: 'Mark games as seen to unlock news',
    batters: 'Batters', pitchers: 'Pitchers',
    player: 'Player', era: 'ERA', whip: 'WHIP',
    spoilerFree: 'SPOILER-FREE',
    chooseDivision: 'Choose a division…',
    otherDivisions: 'OTHER DIVISIONS',
    playerStats: 'PLAYER STATS',
    season: 'Season',
    winPct: 'PCT', gamesBack: 'GB',
    through: 'through',
    noNews: 'No news found',
    allNews: 'All news',
    spoilerMode: 'SPOILER-FREE MODE ACTIVE',
    noGamesMarked: 'No games marked yet.',
    noGamesMarkedDesc: "Mark your first game as 'seen' to unlock the spoiler-free standings and news.",
    gamesTitle: 'GAMES',
    statsSubtitle: 'Spoiler-free through your last seen game',
    newsTitle: 'NEWS',
    newsBefore: 'before',
    newsLatest: 'most recent',
    newsLoading: 'Loading news…',
    standingsLoading: 'Loading standings…',
    chooseDivisionInfo: 'Choose a division to see the standings',
    switchTeam: 'Switch team',
    logout: 'Log out',
  },
  nl: {
    games: 'Wedstrijden', standings: 'Stand', news: 'Nieuws', stats: 'Stats',
    home: 'THUIS', away: 'UIT', seen: 'Gezien', unseen: 'Niet gezien', all: 'Alle',
    markAll: 'Alles markeren', reset: 'Reset', sure: 'Zeker?',
    loading: 'Laden…', error: 'Data kon niet worden geladen.',
    noGames: 'Geen wedstrijden gevonden.',
    lockStandings: 'Markeer wedstrijden om de stand te zien',
    lockStats: 'Markeer wedstrijden om spelersstats te zien',
    lockNews: 'Markeer wedstrijden om nieuws te zien',
    batters: 'Slagmannen', pitchers: 'Werpers',
    player: 'Speler', era: 'ERA', whip: 'WHIP',
    spoilerFree: 'SPOILERVRIJ',
    chooseDivision: 'Kies een divisie…',
    otherDivisions: 'ANDERE DIVISIES',
    playerStats: 'SPELERSSTATS',
    season: 'Seizoen',
    winPct: '%', gamesBack: 'GB',
    through: 't/m',
    noNews: 'Geen nieuws gevonden',
    allNews: 'Al het nieuws',
    spoilerMode: 'SPOILERVRIJE MODUS ACTIEF',
    noGamesMarked: 'Nog geen wedstrijden gemarkeerd.',
    noGamesMarkedDesc: "Markeer je eerste wedstrijd als 'gezien' om de spoilervrije stand en het nieuws te ontgrendelen.",
    gamesTitle: 'WEDSTRIJDEN',
    statsSubtitle: 'Spoilervrij t/m je laatste geziene wedstrijd',
    newsTitle: 'NIEUWS',
    newsBefore: 'vóór',
    newsLatest: 'meest recent',
    newsLoading: 'Nieuws laden…',
    standingsLoading: 'Stand laden…',
    chooseDivisionInfo: 'Kies een divisie om de stand te bekijken',
    switchTeam: 'Wissel van team',
    logout: 'Uitloggen',
  },
  es: {
    games: 'Partidos', standings: 'Clasificación', news: 'Noticias', stats: 'Estadísticas',
    home: 'LOCAL', away: 'VISITANTE', seen: 'Visto', unseen: 'No visto', all: 'Todos',
    markAll: 'Marcar todo', reset: 'Reiniciar', sure: '¿Seguro?',
    loading: 'Cargando…', error: 'No se pudieron cargar los datos.',
    noGames: 'No se encontraron partidos.',
    lockStandings: 'Marca partidos para ver la clasificación',
    lockStats: 'Marca partidos para ver las estadísticas',
    lockNews: 'Marca partidos para ver las noticias',
    batters: 'Bateadores', pitchers: 'Lanzadores',
    player: 'Jugador', era: 'ERA', whip: 'WHIP',
    spoilerFree: 'SIN SPOILERS',
    chooseDivision: 'Elige una división…',
    otherDivisions: 'OTRAS DIVISIONES',
    playerStats: 'ESTADÍSTICAS',
    season: 'Temporada',
    winPct: 'PCT', gamesBack: 'GB',
    through: 'hasta',
    noNews: 'No se encontraron noticias',
    allNews: 'Todas las noticias',
    spoilerMode: 'MODO SIN SPOILERS ACTIVO',
    noGamesMarked: 'Ningún partido marcado aún.',
    noGamesMarkedDesc: "Marca tu primer partido como 'visto' para desbloquear la clasificación y las noticias.",
    gamesTitle: 'PARTIDOS',
    statsSubtitle: 'Sin spoilers hasta tu último partido visto',
    newsTitle: 'NOTICIAS',
    newsBefore: 'antes del',
    newsLatest: 'más reciente',
    newsLoading: 'Cargando noticias…',
    standingsLoading: 'Cargando clasificación…',
    chooseDivisionInfo: 'Elige una división para ver la clasificación',
    switchTeam: 'Cambiar equipo',
    logout: 'Cerrar sesión',
  },
};

const TEAM_BY_ID   = Object.fromEntries(ALL_TEAMS.map(t => [t.id,   t]));
const TEAM_BY_ABBR = Object.fromEntries(ALL_TEAMS.map(t => [t.abbr, t]));
const TEAMS_BY_DIVISION = Object.entries(DIVISIONS).map(([divId, div]) => ({
  divisionId: Number(divId), divisionName: div.name,
  teams: ALL_TEAMS.filter(t => t.divisionId === Number(divId)),
}));

function abbreviateTeam(name) {
  const found = ALL_TEAMS.find(t => t.name === name);
  return found ? found.abbr : name.split(' ').pop().slice(0, 3).toUpperCase();
}

function getDivisionTeams(divisionId) {
  return ALL_TEAMS.filter(t => t.divisionId === divisionId);
}

// ─── FIRESTORE ────────────────────────────────────────────────────────────────

function seenDocRef(uid, abbr)  { return doc(db, 'users', uid, 'teams',   `${abbr}_${SEASON}`); }
function profileDocRef(uid)     { return doc(db, 'users', uid, 'profile', 'settings'); }

async function loadSeenFromFirestore(uid, abbr) {
  try { const s = await getDoc(seenDocRef(uid, abbr)); return s.exists() ? new Set(s.data().seenGames || []) : new Set(); }
  catch { return new Set(); }
}
async function saveSeenToFirestore(uid, abbr, set) {
  try { await setDoc(seenDocRef(uid, abbr), { seenGames: [...set] }, { merge: true }); }
  catch (e) { console.error(e); }
}
async function saveActiveTeam(uid, abbr) {
  try { await setDoc(profileDocRef(uid), { activeTeam: abbr }, { merge: true }); }
  catch (e) { console.error(e); }
}
async function loadActiveTeam(uid) {
  try { const s = await getDoc(profileDocRef(uid)); return s.exists() ? s.data().activeTeam || null : null; }
  catch { return null; }
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function fetchTeamGames(teamId) {
  const res  = await fetch(`${BASE_URL}/schedule?sportId=1&teamId=${teamId}&season=${SEASON}&gameType=R`);
  if (!res.ok) throw new Error(`Schedule API ${res.status}`);
  const data = await res.json();
  const games = [];
  for (const dateObj of data.dates || []) {
    for (const game of dateObj.games) {
      const home = game.teams.home, away = game.teams.away;
      const isHome = home.team.id === teamId;
      const isFinal = game.status.abstractGameState === 'Final';
      const myS  = isHome ? home.score : away.score;
      const oppS = isHome ? away.score : home.score;
      let result = null;
      if (isFinal && myS !== undefined && oppS !== undefined)
        result = myS > oppS ? 'W' : myS < oppS ? 'L' : 'T';
      const opp = isHome ? away.team : home.team;

      // Gebruik dateObj.date (YYYY-MM-DD) als fallback als gameDate ongeldig is
      const rawDate = game.gameDate || game.officialDate || dateObj.date;
      const parsedDate = new Date(rawDate);
      const gameDate = isNaN(parsedDate.getTime())
        ? new Date(dateObj.date) // fallback op dag-datum zonder tijd
        : parsedDate;

      games.push({ id: String(game.gamePk), date: gameDate, result,
        status: game.status.abstractGameState, venue: game.venue?.name || '',
        isHome, opponent: opp.name, opponentAbbr: abbreviateTeam(opp.name),
        myScore: myS, oppScore: oppS });
    }
  }
  return games.sort((a, b) => a.date - b.date);
}

async function fetchAllDivisionGames(divisionId) {
  const results = await Promise.all(
    getDivisionTeams(divisionId).map(t => fetchTeamGames(t.id).then(g => ({ teamId: t.id, games: g })))
  );
  const map = {};
  for (const { teamId, games } of results) map[teamId] = games;
  return map;
}

// Veilige datum-parser: RSS pubDate formaten variëren sterk
function parseRssDate(str) {
  if (!str) return new Date();
  const d = new Date(str);
  // new Date() geeft Invalid Date terug als het formaat niet herkend wordt
  if (!isNaN(d.getTime())) return d;
  // Probeer handmatig te parsen (bijv. "Mon, 31 Mar 2026 12:00:00 +0000")
  const fallback = new Date(str.replace(/\s+\+\d{4}$/, ''));
  return isNaN(fallback.getTime()) ? new Date() : fallback;
}

async function fetchTeamNews(team) {
  // Probeer beide bekende MLB RSS URL-formaten
  const feedUrls = [
    `https://www.mlb.com/${team.slug}/feeds/news/rss.xml`,
    `https://www.mlb.com/${team.slug}/rss/news/rss.xml`,
  ];

  // Poging 1: XML via CORS-proxy's
  for (const feedUrl of feedUrls) {
    try {
      const xml   = await fetchRssXml(feedUrl);
      const items = parseRssXml(xml, team);
      if (items.length > 0) {
        return items;
      }
    } catch (e) {
      console.warn(`${team.abbr} XML proxy mislukt voor ${feedUrl}:`, e.message);
    }
  }

  // Poging 2: rss2json als fallback (werkt soms waar directe proxies falen)
  for (const feedUrl of feedUrls) {
    try {
      const res  = await fetch(`${RSS2JSON_URL}${encodeURIComponent(feedUrl)}&count=50`, { signal: AbortSignal.timeout(7000) });
      if (!res.ok) continue;
      const data = await res.json();
      if (data.status !== 'ok' || !data.items?.length) continue;
      const items = data.items.map(item => {
        const raw  = item.description || item.content || '';
        const text = raw.replace(/<\/p>/gi,'\n\n').replace(/<br\s*\/?>/gi,'\n')
          .replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/&amp;/g,'&')
          .replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&quot;/g,'"').replace(/&#39;/g,"'").trim();
        let img = item.thumbnail || item.enclosure?.link || null;
        if (!img && raw) { const m = raw.match(/<img[^>]+src=["']([^"']+)["']/i); if (m) img = m[1]; }
        return { title: item.title || '', link: item.link, pubDate: parseRssDate(item.pubDate),
          category: `${team.abbr} NEWS`, description: text, image: img };
      }).filter(i => i.title);
      if (items.length > 0) {
        return items;
      }
    } catch (e) {
      console.warn(`${team.abbr} rss2json mislukt:`, e.message);
    }
  }

  console.warn(`${team.abbr} alle nieuws-methodes mislukt, gebruik mock`);
  return [];
}

// ─── FILTER & BEREKENING ──────────────────────────────────────────────────────

function getNextUnseenGameDate(games, seen) {
  // Verzamel alle ongeziene Final-games met geldige datum
  const candidates = games
    .filter(g => !seen.has(g.id) && g.status === 'Final')
    .map(g => ({ ...g, date: g.date instanceof Date ? g.date : new Date(g.date) }))
    .filter(g => !isNaN(g.date.getTime()));

  if (!candidates.length) return null;

  // Sorteer op datum en pak de vroegste
  candidates.sort((a, b) => a.date - b.date);
  const result = candidates[0].date;

  return result;
}

function getLastSeenGame(games, seen) {
  let last = null;
  for (const g of games) if (seen.has(g.id)) last = g;
  return last;
}

function calculateSpoilerFreeStandings(divGames, seen, myGames, myTeamId) {
  if (!seen.size || !Object.keys(divGames).length) return null;
  const last = getLastSeenGame(myGames, seen);
  if (!last) return null;
  const cutoff  = last.date;
  const records = {};
  const teams   = Object.keys(divGames).map(id => TEAM_BY_ID[Number(id)]).filter(Boolean);
  for (const t of teams) records[t.id] = { teamId: t.id, teamAbbr: t.abbr, wins: 0, losses: 0 };
  for (const t of teams) {
    for (const g of (divGames[t.id] || [])) {
      if (g.status !== 'Final' || !g.result || g.date > cutoff) continue;
      if (g.result === 'W') records[t.id].wins++;
      else if (g.result === 'L') records[t.id].losses++;
    }
  }
  return Object.values(records)
    .sort((a, b) => b.wins !== a.wins ? b.wins - a.wins : a.losses - b.losses)
    .map((t, i, arr) => {
      const l   = arr[0];
      const gb  = i === 0 ? '-' : (((l.wins - t.wins) + (t.losses - l.losses)) / 2).toFixed(1);
      const tot = t.wins + t.losses;
      return { ...t, gb, pct: tot > 0 ? (t.wins/tot).toFixed(3).replace('0.','.') : '.000', isMyTeam: t.teamId === myTeamId };
    });
}

function getFilteredNews(news, games, seen) {
  // Sorteer alle artikelen op datum, meest recent eerst
  const allSorted = [...news]
    .filter(a => a.pubDate instanceof Date && !isNaN(a.pubDate))
    .sort((a, b) => b.pubDate - a.pubDate);

  const next = getNextUnseenGameDate(games, seen);

  // Als er geen geldige grens is: toon de 5 meest recente
  if (!next || isNaN(next.getTime())) {
    return allSorted.slice(0, 5);
  }

  const filtered = allSorted.filter(a => a.pubDate < next);

  return (filtered.length > 0 ? filtered : allSorted).slice(0, 5);
}

// ─── TEAM LOGO ────────────────────────────────────────────────────────────────

function TeamLogo({ team, size = 40, className = '' }) {
  const [failed, setFailed] = useState(false);
  // Logo URL is intern gegenereerd — altijd veilig, maar we controleren toch
  const src = `https://www.mlb.com/images/team-logos/${team.abbr.toLowerCase()}.svg`;
  if (failed) {
    return (
      <span className={`team-logo-fallback ${className}`} style={{ width: size, height: size, fontSize: size * 0.38 }}>{team.abbr}</span>
    );
  }
  return (
    <img
      src={src}
      alt={team.name} width={size} height={size}
      className={`team-logo ${className}`}
      onError={() => setFailed(true)}
    />
  );
}

// ─── LOGIN SCHERM ─────────────────────────────────────────────────────────────

function LoginScreen({ onLogin, loading }) {
  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo">
          <span className="logo-tc">MLB</span>
          <div className="logo-text">
            <span className="logo-twins">SPOILER</span>
            <span className="logo-tracker">TRACKER</span>
          </div>
        </div>
        <p className="login-subtitle">Spoilervrij MLB {SEASON} volgen</p>
        <p className="login-desc">Log in om je voortgang op te slaan en te synchroniseren tussen al je apparaten.</p>
        <button className="btn-google" onClick={onLogin} disabled={loading}>
          {loading ? <span className="login-spinner"></span> : (
            <><svg className="google-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>Inloggen met Google</>
          )}
        </button>
        <p className="login-privacy">Alleen je Google-naam en e-mailadres worden opgeslagen.</p>
      </div>
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────

function OnboardingScreen({ onSelectTeam, currentTeam }) {
  const [hovered, setHovered] = useState(null);
  const isSwitch = !!currentTeam;
  return (
    <div className="onboarding-screen">
      <div className="onboarding-inner">
        <h1 className="onboarding-title">{isSwitch ? 'Wissel van team' : 'Kies je team'}</h1>
        <p className="onboarding-subtitle">
          {isSwitch ? 'Je geziene wedstrijden per team worden apart bewaard.' : `Welk team volg je in ${SEASON} spoilervrij?`}
        </p>
        <div className="division-grid">
          {TEAMS_BY_DIVISION.map(({ divisionId, divisionName, teams }) => (
            <div key={divisionId} className="division-block">
              <div className="division-label">{divisionName}</div>
              <div className="team-buttons">
                {teams.map(team => (
                  <button key={team.id}
                    className={`team-btn ${currentTeam?.id === team.id ? 'active' : ''} ${hovered === team.id ? 'hovered' : ''}`}
                    onClick={() => onSelectTeam(team)}
                    onMouseEnter={() => setHovered(team.id)}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <TeamLogo team={team} size={26} className="team-btn-logo" />
                    <span className="team-btn-abbr">{team.abbr}</span>
                    <span className="team-btn-name">{team.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {isSwitch && <button className="btn-cancel-switch" onClick={() => onSelectTeam(null)}>← Terug</button>}
      </div>
    </div>
  );
}

// ─── HOOFD COMPONENT ──────────────────────────────────────────────────────────

export default function App() {
  const [user,           setUser]           = useState(null);
  const [authLoading,    setAuthLoading]    = useState(true);
  const [loginLoading,   setLoginLoading]   = useState(false);
  const [activeTeam,     setActiveTeam]     = useState(null);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const [myGames,        setMyGames]        = useState([]);
  const [divisionGames,  setDivisionGames]  = useState({});
  const [news,           setNews]           = useState([]);
  const [seenGames,      setSeenGames]      = useState(new Set());
  const [filter,         setFilter]         = useState('all');
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [resetConfirm,   setResetConfirm]   = useState(false);
  const [activeTab,      setActiveTab]      = useState('games');
  const [extraDivisionId, setExtraDivisionId] = useState(null);
  const [extraDivisionGames, setExtraDivisionGames] = useState({});
  const [extraDivisionLoading, setExtraDivisionLoading] = useState(false);
  const [lang, setLang] = useState('en');
  const t = TRANSLATIONS[lang];

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fu) => {
      setUser(fu);
      setAuthLoading(false);
      if (fu) {
        const abbr = await loadActiveTeam(fu.uid);
        if (abbr && TEAM_BY_ABBR[abbr]) {
          const team = TEAM_BY_ABBR[abbr];
          setActiveTeam(team);
          applyTheme(team.abbr);
          setSeenGames(await loadSeenFromFirestore(fu.uid, team.abbr));
        }
      } else {
        setActiveTeam(null);
        setSeenGames(new Set());
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user || !activeTeam) return;

    // Debounce: wacht 400ms voordat de fetch start.
    // Als de gebruiker snel van team wisselt worden tussenliggende
    // fetches geannuleerd — dit beschermt de externe RSS-proxy's
    // tegen overbelasting.
    const controller = new AbortController(); // eslint-disable-line no-unused-vars
    let cancelled = false;

    const timer = setTimeout(async () => {
      if (cancelled) return;
      setLoading(true); setError(null); setMyGames([]); setDivisionGames({}); setNews([]);

      try {
        const [games, div] = await Promise.all([
          fetchTeamGames(activeTeam.id),
          fetchAllDivisionGames(activeTeam.divisionId),
        ]);
        if (!cancelled) { setMyGames(games); setDivisionGames(div); }
      } catch {
        if (!cancelled) setError('Data kon niet worden geladen.');
      } finally {
        if (!cancelled) setLoading(false);
      }

      if (!cancelled) {
        fetchTeamNews(activeTeam).then(n => { if (!cancelled) setNews(n); });
      }
    }, 400);

    // Cleanup: annuleer de timer en markeer als geannuleerd
    // als activeTeam wijzigt vóór de 400ms voorbij zijn
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [user, activeTeam]);

  const handleSelectTeam = useCallback(async (team) => {
    if (!team) { setShowTeamPicker(false); return; }
    setShowTeamPicker(false);
    setActiveTeam(team);
    applyTheme(team.abbr);
    if (user) {
      await saveActiveTeam(user.uid, team.abbr);
      setSeenGames(await loadSeenFromFirestore(user.uid, team.abbr));
    }
    setFilter('all');
  }, [user]);

  const handleLogin = useCallback(async () => {
    setLoginLoading(true);
    try { await signInWithPopup(auth, provider); } catch (e) { console.error(e); }
    finally { setLoginLoading(false); }
  }, []);

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    setActiveTeam(null); setMyGames([]); setDivisionGames({}); setNews([]); setSeenGames(new Set());
  }, []);

  const toggleGame = useCallback((id) => {
    if (!user || !activeTeam) return;
    setSeenGames(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      saveSeenToFirestore(user.uid, activeTeam.abbr, next);
      return next;
    });
  }, [user, activeTeam]);

  const laadExtraDivisie = useCallback(async (divisionId) => {
    if (!divisionId) { setExtraDivisionGames({}); return; }
    setExtraDivisionLoading(true);
    try {
      const games = await fetchAllDivisionGames(divisionId);
      setExtraDivisionGames(games);
    } catch (e) {
      console.error('Extra divisie fout:', e);
    } finally {
      setExtraDivisionLoading(false);
    }
  }, []);

  const handleExtraDivisie = useCallback((divisionId) => {
    setExtraDivisionId(divisionId);
    laadExtraDivisie(divisionId);
  }, [laadExtraDivisie]);

  const selectOpponentDivision = useCallback((game) => {
    const opponent = ALL_TEAMS.find(t => 
      t.abbr === game.opponentAbbr || 
      t.name === game.opponent
    );
    if (!opponent) return;
    handleExtraDivisie(opponent.divisionId);
    setTimeout(() => {
      document.querySelector('.panel-standings:last-of-type')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [handleExtraDivisie]);

  const markAllUpTo = useCallback((id, allGames) => {
    if (!user || !activeTeam) return;
    setSeenGames(prev => {
      const next = new Set(prev);
      const idx = allGames.findIndex(g => g.id === id);
      allGames.slice(0, idx + 1).forEach(g => {
        if (g.status === 'Final') next.add(g.id);
      });
      saveSeenToFirestore(user.uid, activeTeam.abbr, next);
      return next;
    });
  }, [user, activeTeam]);

  const markAll = useCallback(() => {
    if (!user || !activeTeam) return;
    setSeenGames(prev => {
      const next = new Set(prev);
      myGames.filter(g => g.status === 'Final').forEach(g => next.add(g.id));
      saveSeenToFirestore(user.uid, activeTeam.abbr, next);
      return next;
    });
  }, [user, activeTeam, myGames]);

  const handleReset = useCallback(() => {
    if (!user || !activeTeam) return;
    if (resetConfirm) {
      const empty = new Set();
      setSeenGames(empty);
      saveSeenToFirestore(user.uid, activeTeam.abbr, empty);
      setResetConfirm(false);
    } else {
      setResetConfirm(true);
      setTimeout(() => setResetConfirm(false), 3000);
    }
  }, [user, activeTeam, resetConfirm]);

  const filteredGames        = myGames.filter(g => filter === 'seen' ? seenGames.has(g.id) : filter === 'unseen' ? !seenGames.has(g.id) : true);
  const spoilerFreeStandings = calculateSpoilerFreeStandings(divisionGames, seenGames, myGames, activeTeam?.id);
  const filteredNews         = getFilteredNews(news, myGames, seenGames);
  const nextUnseenDate       = getNextUnseenGameDate(myGames, seenGames);
  const lastSeenGame         = getLastSeenGame(myGames, seenGames);
  const divisionName         = activeTeam ? DIVISIONS[activeTeam.divisionId]?.name : '';
  const scrollAnchorId = (() => {
    if (!myGames.length) return null;
    const idx = myGames.findIndex(g => !seenGames.has(g.id) && g.status === 'Final');
    return idx === -1 ? lastSeenGame?.id ?? null : myGames[Math.max(0, idx - 4)].id;
  })();

  if (authLoading)  return <div className="auth-loading"><div className="spinner"></div><p>Laden…</p></div>;
  if (!user)        return <LoginScreen onLogin={handleLogin} loading={loginLoading} />;
  if (!activeTeam || showTeamPicker) return <OnboardingScreen onSelectTeam={handleSelectTeam} currentTeam={showTeamPicker ? activeTeam : null} />;

  return (
    <div className="app">
      <Analytics />
      <header className="site-header">
        <div className="header-inner">
          <button className="team-badge-btn" onClick={() => setShowTeamPicker(true)} title={t.switchTeam}>
            <TeamLogo team={activeTeam} size={44} />
            <div className="logo-text">
              <span className="logo-twins">{activeTeam.name.split(' ').slice(0,-1).join(' ').toUpperCase()}</span>
              <span className="logo-tracker">{activeTeam.name.split(' ').slice(-1)[0].toUpperCase()} TRACKER</span>
            </div>
            <span className="team-badge-switch">⇄</span>
          </button>
          <div className="spoiler-badge">
            <span className="badge-dot"></span>
            <span className="badge-label">{t.spoilerMode}</span>
          </div>
          <div className="header-lang">
            {['en','nl','es'].map(l => (
              <button key={l} className={`lang-btn ${lang===l?'active':''}`} onClick={() => setLang(l)}>
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="header-user">
            <img src={user.photoURL} alt={user.displayName} className="user-avatar" referrerPolicy="no-referrer" />
            <span className="user-name">{user.displayName?.split(' ')[0]}</span>
            <button className="btn-logout" onClick={handleLogout} title={t.logout}>↪</button>
          </div>
        </div>
      </header>

      {seenGames.size === 0 && (
        <div className="warning-banner">
          <div className="warning-inner">
            <span className="warning-icon">⚠</span>
            <div className="warning-text">
              <strong>{t.noGamesMarked}</strong>{' '}
              {t.noGamesMarkedDesc}
            </div>
          </div>
        </div>
      )}

      <main className="main-grid">
        <section className="panel panel-games">
          <div className="panel-tabs">
            <button className={`tab-btn ${activeTab==='games'?'active':''}`} onClick={() => setActiveTab('games')}>
              ⚾ {t.games}
            </button>
            <button className={`tab-btn ${activeTab==='stats'?'active':''}`} onClick={() => setActiveTab('stats')}>
              📊 {t.stats}
            </button>
          </div>

          {activeTab === 'games' && <>
            <div className="panel-header">
              <h2>{t.gamesTitle} <span className="season-label">{SEASON}</span></h2>
              <div className="panel-controls">
                <button className="btn-small" onClick={markAll}>{t.markAll}</button>
                <button className={`btn-small btn-danger ${resetConfirm ? 'confirming' : ''}`} onClick={handleReset}>
                  {resetConfirm ? t.sure : t.reset}
                </button>
              </div>
            </div>
            <div className="games-filter">
              {['all','seen','unseen'].map(f => (
                <button key={f} className={`filter-btn ${filter===f?'active':''}`} onClick={() => setFilter(f)}>
                  {f==='all'?t.all:f==='seen'?t.seen:t.unseen}
                </button>
              ))}
            </div>
            <div className="games-list" id="gamesList">
              {loading && <div className="loading-state"><div className="spinner"></div><p>{t.loading}</p></div>}
              {error   && <div className="error-state"><p>⚠ {error}</p></div>}
              {!loading && !error && !filteredGames.length && <div className="empty-state"><p>{t.noGames}</p></div>}
              {!loading && !error && <GamesList games={filteredGames} seenGames={seenGames} onToggle={toggleGame} onMarkAllUpTo={markAllUpTo} onSelectOpponent={selectOpponentDivision} scrollAnchorId={scrollAnchorId} t={t} />}
            </div>
          </>}

          {activeTab === 'stats' && <>
            <div className="panel-header">
              <h2>{t.playerStats} <span className="season-label">{SEASON}</span></h2>
              <p className="stats-subtitle">{t.statsSubtitle}</p>
            </div>
            <PlayerStats activeTeam={activeTeam} lastSeenGame={lastSeenGame} t={t} />
          </>}

        </section>

        <aside className="side-panels">
          <section className="panel panel-standings">
            <div className="panel-header">
              <h2>{divisionName} <span className="spoiler-tag">{t.spoilerFree}</span></h2>
              <span className="standings-date">
                {lastSeenGame ? `${t.through} ${lastSeenGame.opponentAbbr} ${lastSeenGame.date.toLocaleDateString('en-US',{day:'numeric',month:'short'})}` : `${t.through} —`}
              </span>
            </div>
            {!spoilerFreeStandings
              ? <div className="locked-state"><span className="lock-icon">🔒</span><p>{t.lockStandings}</p></div>
              : <StandingsTable data={spoilerFreeStandings} lastSeenGame={lastSeenGame} myTeam={activeTeam} />}
          </section>

          <section className="panel panel-news">
            <div className="panel-header">
              <h2>{activeTeam.abbr} {t.newsTitle} <span className="spoiler-tag">MLB.COM</span></h2>
              <span className="news-cutoff">
                {nextUnseenDate && !isNaN(nextUnseenDate)
                  ? `${t.newsBefore} ${nextUnseenDate.toLocaleDateString('en-US',{day:'numeric',month:'short'})}`
                  : t.newsLatest}
              </span>
            </div>
            {!filteredNews.length
              ? <div className="loading-state"><div className="spinner"></div><p>{t.newsLoading}</p></div>
              : <NewsList articles={filteredNews} nextUnseenDate={nextUnseenDate} />}
          </section>

          <section className="panel panel-standings">
            <div className="panel-header">
              <h2>{t.otherDivisions} <span className="spoiler-tag">{t.spoilerFree}</span></h2>
            </div>
            <div className="division-selector">
              <select
                className="division-select"
                value={extraDivisionId || ''}
                onChange={e => handleExtraDivisie(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{t.chooseDivision}</option>
                {Object.entries(DIVISIONS)
                  .filter(() => true)
                  .map(([id, div]) => (
                    <option key={id} value={id}>{div.name}</option>
                  ))
                }
              </select>
            </div>
            {extraDivisionLoading && (
              <div className="loading-state"><div className="spinner"></div><p>{t.standingsLoading}</p></div>
            )}
            {!extraDivisionLoading && extraDivisionId && (() => {
              const stand = calculateSpoilerFreeStandings(extraDivisionGames, seenGames, myGames, null);
              if (!stand) return <div className="locked-state"><span className="lock-icon">🔒</span><p>{t.lockStandings}</p></div>;
              return (
                <>
                  <StandingsTable data={stand} lastSeenGame={lastSeenGame} myTeam={activeTeam} />
                </>
              );
            })()}
            {!extraDivisionId && (
              <div className="locked-state">
                <span className="lock-icon">🏟</span>
                <p>{t.chooseDivisionInfo}</p>
              </div>
            )}
          </section>
        </aside>
      </main>
    </div>
  );
}

// ─── SUB-COMPONENTEN ──────────────────────────────────────────────────────────

function PlayerStats({ activeTeam, lastSeenGame, t }) {
  const [batters, setBatters] = useState([]);
  const [pitchers, setPitchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGroup, setActiveGroup] = useState('batting');
  const [sortBat, setSortBat] = useState({ key: 'ops', asc: false });
  const [sortPitch, setSortPitch] = useState({ key: 'era', asc: true });

  useEffect(() => {
    if (!activeTeam || !lastSeenGame) { setLoading(false); return; }
    laadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeam, lastSeenGame]);

  async function laadStats() {
    setLoading(true);
    try {
      const datumStr = lastSeenGame.date.toISOString().split('T')[0];

      const rosterRes = await fetch(`https://statsapi.mlb.com/api/v1/teams/${activeTeam.id}/roster?rosterType=active&season=2026`);
      const rosterData = await rosterRes.json();
      const spelerIds = rosterData.roster.map(p => p.person.id);

      const battingRes = await fetch(
        `https://statsapi.mlb.com/api/v1/stats?stats=season&season=2026&group=hitting&playerPool=All&teamId=${activeTeam.id}&endDate=${datumStr}&hydrate=person`
      );
      const battingData = await battingRes.json();
      const battersList = (battingData.stats?.[0]?.splits || [])
        .filter(s => spelerIds.includes(s.player.id))
        .map(s => ({
          naam: s.player.fullName,
          avg: s.stat.avg || '.000',
          hr: s.stat.homeRuns || 0,
          rbi: s.stat.rbi || 0,
          ops: s.stat.ops || '.000',
          games: s.stat.gamesPlayed || 0,
        }));

      const pitchingRes = await fetch(
        `https://statsapi.mlb.com/api/v1/stats?stats=season&season=2026&group=pitching&playerPool=All&teamId=${activeTeam.id}&endDate=${datumStr}&hydrate=person`
      );
      const pitchingData = await pitchingRes.json();
      const pitchersList = (pitchingData.stats?.[0]?.splits || [])
        .filter(s => spelerIds.includes(s.player.id))
        .map(s => ({
          naam: s.player.fullName,
          era: s.stat.era || '9.99',
          whip: s.stat.whip || '9.99',
          wins: s.stat.wins || 0,
          losses: s.stat.losses || 0,
          so: s.stat.strikeOuts || 0,
          games: s.stat.gamesPlayed || 0,
        }));

      setBatters(battersList);
      setPitchers(pitchersList);
    } catch (e) {
      console.error('Stats fout:', e);
    } finally {
      setLoading(false);
    }
  }

  function sortData(data, key, asc) {
    return [...data].sort((a, b) => {
      const valA = parseFloat(a[key]) || (typeof a[key] === 'string' ? a[key] : 0);
      const valB = parseFloat(b[key]) || (typeof b[key] === 'string' ? b[key] : 0);
      if (typeof valA === 'string') return asc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      return asc ? valA - valB : valB - valA;
    });
  }

  function toggleSortBat(key) {
    setSortBat(prev => ({ key, asc: prev.key === key ? !prev.asc : false }));
  }

  function toggleSortPitch(key) {
    setSortPitch(prev => ({ key, asc: prev.key === key ? !prev.asc : true }));
  }

  function SortIcon({ col, sort }) {
    if (sort.key !== col) return <span style={{color:'#555'}}> ⇅</span>;
    return <span style={{color:'#D31145'}}> {sort.asc ? '↑' : '↓'}</span>;
  }

  if (!lastSeenGame) {
    return (
      <div className="locked-state">
        <span className="lock-icon">🔒</span>
        <p>{t.lockStats}</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading-state"><div className="spinner"></div><p>{t.loading}</p></div>;
  }

  const sortedBatters = sortData(batters, sortBat.key, sortBat.asc);
  const sortedPitchers = sortData(pitchers, sortPitch.key, sortPitch.asc);

  return (
    <div className="player-stats">
      <div className="stats-tabs">
        <button className={`stats-tab ${activeGroup==='batting'?'active':''}`} onClick={() => setActiveGroup('batting')}>
          {t.batters}
        </button>
        <button className={`stats-tab ${activeGroup==='pitching'?'active':''}`} onClick={() => setActiveGroup('pitching')}>
          {t.pitchers}
        </button>
      </div>

      {activeGroup === 'batting' && (
        <table className="stats-table">
          <thead>
            <tr>
              <th onClick={() => toggleSortBat('naam')} className="sortable">Speler<SortIcon col="naam" sort={sortBat} /></th>
              <th onClick={() => toggleSortBat('games')} className="sortable">G<SortIcon col="games" sort={sortBat} /></th>
              <th onClick={() => toggleSortBat('avg')} className="sortable">AVG<SortIcon col="avg" sort={sortBat} /></th>
              <th onClick={() => toggleSortBat('hr')} className="sortable">HR<SortIcon col="hr" sort={sortBat} /></th>
              <th onClick={() => toggleSortBat('rbi')} className="sortable">RBI<SortIcon col="rbi" sort={sortBat} /></th>
              <th onClick={() => toggleSortBat('ops')} className="sortable">OPS<SortIcon col="ops" sort={sortBat} /></th>
            </tr>
          </thead>
          <tbody>
            {sortedBatters.map((b, i) => (
              <tr key={i}>
                <td>{b.naam}</td>
                <td>{b.games}</td>
                <td>{b.avg}</td>
                <td>{b.hr}</td>
                <td>{b.rbi}</td>
                <td>{b.ops}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {activeGroup === 'pitching' && (
        <table className="stats-table">
          <thead>
            <tr>
              <th onClick={() => toggleSortPitch('naam')} className="sortable">Speler<SortIcon col="naam" sort={sortPitch} /></th>
              <th onClick={() => toggleSortPitch('games')} className="sortable">G<SortIcon col="games" sort={sortPitch} /></th>
              <th onClick={() => toggleSortPitch('wins')} className="sortable">W-L<SortIcon col="wins" sort={sortPitch} /></th>
              <th onClick={() => toggleSortPitch('era')} className="sortable">ERA<SortIcon col="era" sort={sortPitch} /></th>
              <th onClick={() => toggleSortPitch('whip')} className="sortable">WHIP<SortIcon col="whip" sort={sortPitch} /></th>
              <th onClick={() => toggleSortPitch('so')} className="sortable">SO<SortIcon col="so" sort={sortPitch} /></th>
            </tr>
          </thead>
          <tbody>
            {sortedPitchers.map((p, i) => (
              <tr key={i}>
                <td>{p.naam}</td>
                <td>{p.games}</td>
                <td>{p.wins}-{p.losses}</td>
                <td>{p.era}</td>
                <td>{p.whip}</td>
                <td>{p.so}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function GamesList({ games, seenGames, onToggle, onMarkAllUpTo, onSelectOpponent, scrollAnchorId, t }) {
  const anchorRef   = useRef(null);
  const hasScrolled = useRef(false);
  useEffect(() => { if (games.length) hasScrolled.current = false; }, [games]);
  useEffect(() => {
    if (!anchorRef.current || hasScrolled.current) return;
    hasScrolled.current = true;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const row = anchorRef.current; if (!row) return;
      const c   = document.getElementById('gamesList'); if (!c) return;
      c.scrollTop = Math.max(0, row.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop);
    }));
  });
  let lastMonth = '';
  return (
    <>
      {games.map(game => {
        const month = game.date.toLocaleString('nl-NL', { month: 'long', year: 'numeric' });
        const showDiv = month !== lastMonth; lastMonth = month;
        return (
          <div key={game.id}>
            {showDiv && <div className="month-divider">{month.toUpperCase()}</div>}
            <GameRow game={game} seen={seenGames.has(game.id)} onToggle={onToggle} onMarkAllUpTo={onMarkAllUpTo} onSelectOpponent={onSelectOpponent} allGames={games} anchorRef={game.id === scrollAnchorId ? anchorRef : null} t={t} />
          </div>
        );
      })}
    </>
  );
}

function GameRow({ game, seen, onToggle, onMarkAllUpTo, onSelectOpponent, allGames, anchorRef, t }) {
  const day = game.date.getDate().toString().padStart(2,'0');
  const mon = game.date.toLocaleString('nl-NL',{month:'short'}).toUpperCase();
  const isFinal = game.status === 'Final';
  let scoreEl;
  if (isFinal) {
    if (seen) {
      const cls = game.result==='W'?'win':game.result==='L'?'loss':'tie';
      scoreEl = <div className={`score-visible ${cls}`}>{game.myScore}–{game.oppScore} <small>{game.result}</small></div>;
    } else scoreEl = <div className="score-hidden">● ● ●</div>;
  } else if (game.status === 'Live') {
    scoreEl = <div className="score-scheduled live">LIVE</div>;
  } else {
    scoreEl = <div className="score-scheduled">{game.date.toLocaleTimeString('nl-NL',{hour:'2-digit',minute:'2-digit'})}</div>;
  }
  return (
<div className={`game-row ${seen?'seen':'unseen'}`} ref={anchorRef||null} onClick={() => onSelectOpponent(game)} style={{cursor:'pointer'}}>
      <div className="game-date"><span className="month">{mon}</span><span className="day">{day}</span></div>
      <div className="game-info">
        <div className="game-matchup">
          <span className="home-away">{game.isHome?t.home:t.away}</span>
          {game.isHome ? game.opponentAbbr : <><span className="at-sign">@</span>{game.opponentAbbr}</>}
        </div>
        <div className="game-venue">{game.venue}</div>
      </div>
      <div className="game-score">{scoreEl}</div>
      <div className="game-toggle">
  <button className={`toggle-btn ${seen?'seen':''}`} onClick={(e) => { e.stopPropagation(); onToggle(game.id); }} disabled={!isFinal} title={seen?'Markeer als niet gezien':'Markeer als gezien'}>
    {seen?'✓':'○'}
  </button>
  {isFinal && !seen && (
    <button className="mark-all-btn" onClick={(e) => { e.stopPropagation(); onMarkAllUpTo(game.id, allGames); }} title="Markeer alles t/m hier als gezien">
      ⤓
    </button>
  )}
</div>
    </div>
  );
}

function StandingsTable({ data, lastSeenGame, myTeam }) {
  return (
    <>
      <div className="standings-table">
        <div className="standings-row header"><span></span><span>TEAM</span><span>W</span><span>V</span><span>%</span><span>GB</span></div>
        {data.map((t, i) => (
          <div key={t.teamId} className={`standings-row ${t.isMyTeam?'my-team-row':''}`}>
            <span className="rank">{i+1}</span>
            <span className="team-name">{t.teamAbbr}{t.isMyTeam?' ★':''}</span>
            <span className="stat">{t.wins}</span><span className="stat">{t.losses}</span>
            <span className="stat">{t.pct}</span><span className="gb">{t.gb}</span>
          </div>
        ))}
      </div>
      <p className="standings-note">
        {lastSeenGame ? `Na ${myTeam.abbr} ${lastSeenGame.isHome?'vs':'@'} ${lastSeenGame.opponentAbbr} · ${lastSeenGame.date.toLocaleDateString('nl-NL',{day:'numeric',month:'short'})}` : 'Stand op basis van geziene wedstrijden'}
      </p>
    </>
  );
}

function NewsList({ articles, nextUnseenDate }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="news-list">
      {articles.map((a, i) => {
        const isOpen  = openId === i;
        const dateStr = a.pubDate.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'});
        return (
          <div key={i} className={`news-card ${isOpen?'open':''}`}>
            <div className="news-card-header" onClick={() => setOpenId(p => p===i?null:i)}>
              <div className="news-card-meta"><span className="news-category">{a.category}</span><span className="news-date">{dateStr}</span></div>
              <div className="news-card-title">{a.title}</div>
              <span className="news-card-chevron">{isOpen?'▲':'▼'}</span>
            </div>
            {isOpen && (
              <div className="news-card-body">
                {isSafeImageUrl(a.image) && (
                  <img src={a.image} alt="" className="news-card-image" onError={e=>{e.target.style.display='none';}} />
                )}
                <p className="news-card-description">{a.description||'Samenvatting niet beschikbaar.'}</p>
                <a
                  href={isSafeUrl(a.link) ? a.link : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-card-readmore"
                >
                  Read full article on MLB.com →
                </a>
              </div>
            )}
          </div>
        );
      })}
      <p className="news-filtered-note">
        📰 Official MLB feed · 5 most recent articles
        {nextUnseenDate && ` · before ${nextUnseenDate.toLocaleDateString('en-US',{month:'short',day:'numeric'})}`}
      </p>
    </div>
  );
}
